import { ForwardedRef, PropsWithChildren, forwardRef } from 'react';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogTriggerProps } from '@radix-ui/react-dialog';
import * as Dialog from 'components/dialog';
import { twMerge } from 'tailwind-merge';

interface EducationalContentDialogTriggerProps extends Omit<DialogTriggerProps, 'color'> {}
const EducationalContentDialogTrigger = forwardRef(
    (
        { className, children, ...rest }: EducationalContentDialogTriggerProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        return (
            <Dialog.Trigger asChild ref={forwardedRef}>
                <Button
                    variant="none"
                    color="primary"
                    size="sm"
                    className={twMerge('font-medium hover:underline', className)}
                    {...rest}
                >
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <span>{children}</span>
                </Button>
            </Dialog.Trigger>
        );
    },
);

interface EducationalContentDialogContentProps extends PropsWithChildren {}
const EducationalContentDialogContent = ({ children }: EducationalContentDialogContentProps) => {
    return (
        <Dialog.Portal>
            <Dialog.Overlay />
            <Dialog.Content className="flex max-w-[400px] flex-col gap-8 p-6">
                <div className="flex flex-col gap-4">
                    <BoxIcon size="lg">
                        <FontAwesomeIcon className="text-2xl" icon={faCircleInfo} />
                    </BoxIcon>
                    {children}
                </div>

                <Dialog.Cancel asChild>
                    <Button variant="outline" color="neutral" className="w-full">
                        Close
                    </Button>
                </Dialog.Cancel>
                <Dialog.Close />
            </Dialog.Content>
        </Dialog.Portal>
    );
};

interface EducationalContentDialogHeaderProps extends PropsWithChildren {}
const EducationalContentDialogHeader = ({ children }: EducationalContentDialogHeaderProps) => {
    return <div className="flex flex-col gap-1 text-sm text-gray-600">{children}</div>;
};

interface EducationalContentDialogTitleProps extends PropsWithChildren {}
const EducationalContentDialogTitle = ({ children }: EducationalContentDialogTitleProps) => {
    return <h2 className="text-lg font-semibold text-gray-900">{children}</h2>;
};

const Root = Dialog.Root;
const Trigger = EducationalContentDialogTrigger;
const Content = EducationalContentDialogContent;
const Header = EducationalContentDialogHeader;
const Title = EducationalContentDialogTitle;

export {
    EducationalContentDialogTrigger,
    EducationalContentDialogContent,
    EducationalContentDialogHeader,
    EducationalContentDialogTitle,
    //
    Root,
    Trigger,
    Content,
    Header,
    Title,
};
